'use client';

import classNames from 'classnames';
import { type ComponentProps, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import { useClientNavigationSearchParams } from '@alltrails/core/hooks/useClientNavigation';
import useWindowSize from '@alltrails/core/hooks/useWindowSize';
import Button from '@alltrails/denali/components/Button';
import NavBar from '@alltrails/denali/components/NavBar';
import { usePageWidth } from '@alltrails/denali/hooks/usePageWidth';
import { isValidTabId } from '../../utils/isValidTabId';
import styles from './styles/styles.module.scss';
import type { NavItem } from './types';

export type ScrollerNavProps = {
  items: NavItem[];
  title: string | ReactNode;
  selectedTab: string;
  onTabChange: (id: string) => void;
  ctaInfo?: ComponentProps<typeof Button>;
};

const desktopWidth = 1024;

export const ScrollerNavInternal = ({ items, title, selectedTab, onTabChange, ctaInfo }: ScrollerNavProps) => {
  const searchParams = useClientNavigationSearchParams();
  const { width } = useWindowSize();
  const pageWidth = usePageWidth();
  const [isInitialScrollDone, setIsInitialScrollDone] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    if (isInitialScrollDone) {
      return;
    }

    const sectionId = window.location.hash.slice(1);

    if (sectionId && isValidTabId(sectionId, items)) {
      onTabChange(sectionId);
    }

    setIsInitialScrollDone(true);
  }, [onTabChange, isInitialScrollDone, items, searchParams]);

  const ctaButton = useMemo(() => {
    if (!ctaInfo) {
      return null;
    }

    return (
      <div className={styles.ctaButtonContainer}>
        <Button
          text={ctaInfo.text}
          className={styles.ctaButton}
          onClick={ctaInfo.onClick}
          linkInfo={ctaInfo?.linkInfo}
          size={['md', 'lg'].includes(String(pageWidth)) ? 'md' : 'sm'}
          variant="primary"
        />
      </div>
    );
  }, [ctaInfo, pageWidth]);

  const navBar = useMemo(() => {
    return (
      <NavBar
        activePath={selectedTab}
        className={styles.tabBar}
        hideBottomBorder
        onNavigate={onTabChange}
        navItems={items.map(item => ({
          id: item.id,
          label: item.title,
          href: `#${item.id}`
        }))}
        ariaLabel={intl.formatMessage({
          defaultMessage: 'Page sections navigation'
        })}
      />
    );
  }, [onTabChange, selectedTab, items, intl]);

  if (width < desktopWidth) {
    return (
      <>
        <div className={styles.navContainer}>
          <div className={styles.navContent}>
            <Typography variant="heading400" component="h2">
              {title}
            </Typography>
          </div>
        </div>
        <div className={styles.navContainer}>
          <div className={classNames(styles.navContent, styles.navBar)}>
            {navBar}
            {ctaButton}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className={styles.navContainer}>
      <div className={classNames(styles.navContent, styles.navBar)}>
        <Typography variant="heading400" component="h2">
          {title}
        </Typography>
        {navBar}
        {ctaButton}
      </div>
    </div>
  );
};
